import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

interface ProfileForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  bizNumber: string;
}

interface PasswordForm {
  current: string;
  next: string;
  confirm: string;
}

interface ProfileData {
  full_name: string | null;
  phone: string | null;
  email: string | null;
  credit: number;
  plan: string | null;
}

export default function AccountTab() {
  const { user, session, perMessageRate } = useAuth();

  const [profile, setProfile] = useState<ProfileForm>({
    name: "",
    email: "",
    phone: "",
    company: "",
    bizNumber: "",
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileErrors, setProfileErrors] = useState<Partial<ProfileForm>>({});

  const [realCredit, setRealCredit] = useState(0);
  const [realPlan, setRealPlan] = useState("Basic");

  const [pwForm, setPwForm] = useState<PasswordForm>({ current: "", next: "", confirm: "" });
  const [pwErrors, setPwErrors] = useState<Partial<PasswordForm>>({});
  const [pwSaved, setPwSaved] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");

  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // Load real profile data
  const loadProfile = useCallback(async () => {
    if (!user || !session) return;
    setProfileLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/get-user-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
      const data = await res.json();
      if (data.error) {
        console.error("[AccountTab] profile load error:", data.error);
        // Fallback to auth metadata
        setProfile({
          name: (user.user_metadata?.full_name as string) ?? (user.email?.split("@")[0] || ""),
          email: user.email ?? "",
          phone: (user.user_metadata?.phone as string) ?? "",
          company: "",
          bizNumber: "",
        });
        setRealCredit(0);
        setRealPlan("Basic");
      } else {
        const p: ProfileData = data;
        setProfile({
          name: p.full_name ?? (user.user_metadata?.full_name as string) ?? (user.email?.split("@")[0] || ""),
          email: p.email ?? user.email ?? "",
          phone: p.phone ?? (user.user_metadata?.phone as string) ?? "",
          company: "",
          bizNumber: "",
        });
        setRealCredit(p.credit ?? 0);
        setRealPlan(p.plan ?? "Basic");
      }
    } catch (err) {
      console.error("[AccountTab] profile load exception:", err);
    } finally {
      setProfileLoading(false);
    }
  }, [user, session?.access_token]);

  useEffect(() => {
    if (user && session) loadProfile();
  }, [user, session, loadProfile]);

  const validateProfile = () => {
    const errors: Partial<ProfileForm> = {};
    if (!profile.name.trim()) errors.name = "이름을 입력해주세요";
    if (!profile.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) errors.email = "올바른 이메일을 입력해주세요";
    return errors;
  };

  const handleProfileSave = async () => {
    const errors = validateProfile();
    if (Object.keys(errors).length > 0) { setProfileErrors(errors); return; }
    setProfileErrors({});

    if (!user || !session) {
      showToast("로그인이 필요합니다", "error");
      return;
    }

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/update-user-profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          user_id: user.id,
          full_name: profile.name.trim(),
          phone: profile.phone.trim() || null,
        }),
      });
      const result = await res.json();
      if (!res.ok || result.error) {
        throw new Error(result.error || "저장 실패");
      }
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
      showToast("계정 정보가 저장되었습니다");
      // Refresh credit in case it changed
      if (result.profile?.credit !== undefined) {
        setRealCredit(result.profile.credit);
      }
    } catch (err: any) {
      showToast(err?.message || "저장 중 오류가 발생했습니다", "error");
    }
  };

  const validatePw = () => {
    const errors: Partial<PasswordForm> = {};
    if (!pwForm.current) errors.current = "현재 비밀번호를 입력해주세요";
    if (pwForm.next.length < 8) errors.next = "비밀번호는 8자 이상이어야 합니다";
    if (pwForm.next !== pwForm.confirm) errors.confirm = "비밀번호가 일치하지 않습니다";
    return errors;
  };

  const handlePwSave = async () => {
    const errors = validatePw();
    if (Object.keys(errors).length > 0) { setPwErrors(errors); return; }
    setPwErrors({});

    if (!session) {
      showToast("세션이 만료되었습니다. 다시 로그인해주세요", "error");
      return;
    }

    setPwLoading(true);
    try {
      // First verify current password by signing in again
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email ?? "",
        password: pwForm.current,
      });
      if (signInError) {
        throw new Error("현재 비밀번호가 올바르지 않습니다");
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: pwForm.next,
      });
      if (updateError) {
        throw new Error(updateError.message);
      }

      setPwForm({ current: "", next: "", confirm: "" });
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 2000);
      showToast("비밀번호가 변경되었습니다");
    } catch (err: any) {
      showToast(err?.message || "비밀번호 변경 중 오류가 발생했습니다", "error");
    } finally {
      setPwLoading(false);
    }
  };

  const getPwStrength = (pw: string) => {
    if (!pw) return { level: 0, label: "", color: "" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: score, label: "약함", color: "bg-red-500" };
    if (score <= 3) return { level: score, label: "보통", color: "bg-amber-500" };
    return { level: score, label: "강함", color: "bg-emerald-500" };
  };

  const pwStrength = getPwStrength(pwForm.next);

  const formatPhone = (val: string) => {
    const digits = val.replace(/[^0-9]/g, "");
    if (digits.length <= 7) return digits.replace(/(\d{3})(\d{0,4})/, "$1-$2");
    return digits.replace(/(\d{3})(\d{3,4})(\d{0,4})/, "$1-$2-$3");
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-white font-bold text-lg">계정 정보</h2>
        <p className="text-gray-500 text-sm mt-0.5">프로필 및 보안 설정을 관리합니다</p>
      </div>

      {/* Avatar + plan */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4 sm:p-5">
        <div className="relative">
          <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 text-white text-xl sm:text-2xl font-bold shrink-0">
            {profileLoading ? (
              <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
            ) : (
              profile.name.charAt(0) || "?"
            )}
          </div>
          <button className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-[#1a1a2e] border border-white/[0.15] text-gray-400 hover:text-white cursor-pointer transition-colors">
            <i className="ri-pencil-line text-[10px] sm:text-xs"></i>
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm sm:text-base">{profileLoading ? "불러오는 중..." : profile.name}</p>
          <p className="text-gray-500 text-xs sm:text-sm">{profile.email}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500/20 to-pink-500/20 border border-violet-500/30 text-violet-300 text-[11px] sm:text-xs font-semibold whitespace-nowrap">
              <i className="ri-vip-crown-line mr-1"></i>
              {realPlan} 플랜
            </span>
            <span className="text-gray-600 text-[11px] sm:text-xs">· 설정에서 변경 가능</span>
          </div>
        </div>
        <div className="text-left sm:text-right shrink-0 w-full sm:w-auto flex sm:block items-center gap-4 sm:gap-0">
          <div>
            <p className="text-gray-500 text-[11px] sm:text-xs">잔여 크레딧</p>
            <p className="text-white font-bold text-lg sm:text-xl">{profileLoading ? "-" : realCredit.toLocaleString()}</p>
          </div>
          <p className="text-gray-600 text-[11px] sm:text-xs">{profileLoading ? "" : `≈ SMS ${Math.floor(realCredit / (perMessageRate ?? 20)).toLocaleString()}건`}</p>
        </div>
      </div>

      {/* Profile form */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4 sm:p-6">
        <h3 className="text-white font-semibold text-sm mb-4 sm:mb-5 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-user-line text-violet-400 text-base"></i>
          </div>
          기본 정보
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {[
            { key: "name", label: "이름", placeholder: "홍길동", required: true },
            { key: "email", label: "이메일", placeholder: "hong@example.com", required: true, readOnly: true },
            { key: "phone", label: "연락처", placeholder: "010-0000-0000", required: false },
            { key: "company", label: "회사명", placeholder: "회사명 입력", required: false },
          ].map(({ key, label, placeholder, required, readOnly }) => (
            <div key={key}>
              <label className="block text-gray-400 text-xs font-medium mb-1.5">
                {label} {required && <span className="text-red-400">*</span>}
                {readOnly && <span className="text-gray-600 ml-1">(변경 불가)</span>}
              </label>
              <input
                type={key === "email" ? "email" : "text"}
                value={profile[key as keyof ProfileForm]}
                readOnly={readOnly}
                onChange={(e) => {
                  const val = key === "phone" ? formatPhone(e.target.value) : e.target.value;
                  setProfile((p) => ({ ...p, [key]: val }));
                  setProfileErrors((p) => ({ ...p, [key]: undefined }));
                }}
                placeholder={placeholder}
                className={`w-full bg-white/[0.04] border rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none transition-all ${
                  readOnly
                    ? "border-white/[0.04] text-gray-500 cursor-not-allowed"
                    : profileErrors[key as keyof ProfileForm]
                      ? "border-red-500/50 focus:border-red-500"
                      : "border-white/[0.08] focus:border-violet-500/50"
                }`}
              />
              {profileErrors[key as keyof ProfileForm] && (
                <p className="text-red-400 text-xs mt-1">{profileErrors[key as keyof ProfileForm]}</p>
              )}
            </div>
          ))}
          <div className="col-span-2">
            <label className="block text-gray-400 text-xs font-medium mb-1.5">사업자 등록번호</label>
            <input
              type="text"
              value={profile.bizNumber}
              onChange={(e) => setProfile((p) => ({ ...p, bizNumber: e.target.value }))}
              placeholder="000-00-00000"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
            />
            <p className="text-gray-600 text-xs mt-1">회사명과 사업자등록번호는 현재 프로필에 저장되지 않습니다.</p>
          </div>
        </div>
        <div className="flex justify-end mt-5">
          <button
            onClick={handleProfileSave}
            disabled={profileSaved || profileLoading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
              profileSaved
                ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400"
                : "bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white hover:scale-105 disabled:opacity-50"
            }`}
          >
            <i className={`${profileSaved ? "ri-checkbox-circle-line" : "ri-save-line"} text-base`}></i>
            {profileSaved ? "저장됨" : "저장"}
          </button>
        </div>
      </div>

      {/* Password form */}
      <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4 sm:p-6">
        <h3 className="text-white font-semibold text-sm mb-4 sm:mb-5 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-lock-line text-violet-400 text-base"></i>
          </div>
          비밀번호 변경
        </h3>
        <div className="space-y-4">
          {(["current", "next", "confirm"] as const).map((field) => {
            const labels = { current: "현재 비밀번호", next: "새 비밀번호", confirm: "새 비밀번호 확인" };
            const placeholders = { current: "현재 비밀번호 입력", next: "8자 이상 입력", confirm: "비밀번호 재입력" };
            return (
              <div key={field}>
                <label className="block text-gray-400 text-xs font-medium mb-1.5">{labels[field]}</label>
                <div className="relative">
                  <input
                    type={showPw[field] ? "text" : "password"}
                    value={pwForm[field]}
                    onChange={(e) => {
                      setPwForm((p) => ({ ...p, [field]: e.target.value }));
                      setPwErrors((p) => ({ ...p, [field]: undefined }));
                    }}
                    placeholder={placeholders[field]}
                    className={`w-full bg-white/[0.04] border rounded-xl px-4 py-2.5 pr-10 text-white text-sm placeholder-gray-600 focus:outline-none transition-all ${
                      pwErrors[field] ? "border-red-500/50 focus:border-red-500" : "border-white/[0.08] focus:border-violet-500/50"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((p) => ({ ...p, [field]: !p[field] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors"
                  >
                    <i className={`${showPw[field] ? "ri-eye-off-line" : "ri-eye-line"} text-sm`}></i>
                  </button>
                </div>
                {pwErrors[field] && <p className="text-red-400 text-xs mt-1">{pwErrors[field]}</p>}
                {field === "next" && pwForm.next && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${pwStrength.color}`}
                          style={{ width: `${(pwStrength.level / 5) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-xs font-medium ${pwStrength.color.replace("bg-", "text-")}`}>{pwStrength.label}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-5">
          <button
            onClick={handlePwSave}
            disabled={pwSaved || pwLoading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
              pwSaved
                ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400"
                : "bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white hover:scale-105 disabled:opacity-50"
            }`}
          >
            {pwLoading ? (
              <><i className="ri-loader-4-line animate-spin text-base"></i>변경 중...</>
            ) : pwSaved ? (
              <><i className="ri-checkbox-circle-line text-base"></i>변경됨</>
            ) : (
              <><i className="ri-lock-password-line text-base"></i>비밀번호 변경</>
            )}
          </button>
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 sm:p-6">
        <h3 className="text-red-400 font-semibold text-sm mb-2 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-400 text-base"></i>
          </div>
          위험 구역
        </h3>
        <p className="text-gray-500 text-xs mb-4">계정을 삭제하면 모든 데이터가 영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.</p>
        <button
          onClick={() => setDeleteConfirm(true)}
          className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-sm font-medium cursor-pointer whitespace-nowrap transition-all"
        >
          <i className="ri-delete-bin-line mr-1.5"></i>
          계정 삭제
        </button>
      </div>

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteConfirm(false)}></div>
          <div className="relative bg-[#0d0d1a] border border-white/[0.1] rounded-t-2xl md:rounded-2xl p-5 md:p-6 w-full md:max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-4">
              <i className="ri-error-warning-line text-red-400 text-2xl"></i>
            </div>
            <h3 className="text-white font-bold text-base text-center mb-2">정말 계정을 삭제할까요?</h3>
            <p className="text-gray-400 text-sm text-center mb-5">확인을 위해 아래에 <span className="text-red-400 font-semibold">DELETE</span>를 입력해주세요</p>
            <input
              type="text"
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="DELETE"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-500/50 transition-all mb-4"
            />
            <div className="flex gap-3">
              <button onClick={() => { setDeleteConfirm(false); setDeleteInput(""); }} className="flex-1 py-2.5 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all">취소</button>
              <button
                onClick={() => { if (deleteInput === "DELETE") showToast("계정 삭제 요청이 접수되었습니다", "error"); setDeleteConfirm(false); setDeleteInput(""); }}
                disabled={deleteInput !== "DELETE"}
                className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-sm font-bold cursor-pointer whitespace-nowrap transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[60] flex items-center gap-2.5 px-4 py-3 rounded-xl border ${
          toast.type === "success" ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-300" : "bg-red-500/20 border-red-500/30 text-red-300"
        }`}>
          <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} text-base`}></i>
          <span className="text-sm font-medium">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}